'use client';

import React from 'react';
import * as R from 'ramda';
import { decimalsToWei } from '@akropolis-web/primitives';

import { AssetsInfo, MarketsTableData, RiskConcentrationData } from 'api/selectors/pages';
import { amountToAccessorValue } from 'components/MRT';
import { styled } from 'core/styles';
import { getAmount } from 'domain/utils';
import { RouteUtils, useRouteUtils } from 'routes';
import { toEither } from 'utils/either';
import { MarketIcon, ProtocolIcon, TokenIcon } from 'components';
import { ProductSlug, VersionSlug, NetworkSlug } from 'domain/types';

import { TvlTreemap } from './TvlTreemap';
import { TvlTreemapData, TvlTreemapLeaf } from './types';

type Props = {
  title: string;
  productSlug?: ProductSlug;
  versionSlug?: VersionSlug;
  network?: NetworkSlug;
  data:
    | { type: 'assets'; info: AssetsInfo[] }
    | { type: 'markets'; info: MarketsTableData[] }
    | { type: 'account-positions'; info: RiskConcentrationData[] };
};

export function TvlTreemapWidget({ title, productSlug, versionSlug, network, data }: Props) {
  const { redirectTo } = useRouteUtils();

  return (
    <Root>
      <Title>{title}</Title>
      <AssetsTreemapWrapper>
        <TvlTreemap
          data={getData({ data, productSlug, versionSlug, network, routeRedirectTo: redirectTo })}
        />
      </AssetsTreemapWrapper>
    </Root>
  );
}

function getData({
  data,
  productSlug,
  versionSlug,
  network,
  routeRedirectTo,
}: Omit<Props, 'title'> & { routeRedirectTo: RouteUtils['redirectTo'] }): TvlTreemapData[] {
  const allLeafs = getLeafs(data, routeRedirectTo).sort(
    (a, b) => (b.percent || 0) - (a.percent || 0),
  );

  const groupedLeafs = R.groupBy<TvlTreemapLeaf, 'main' | 'other'>(
    leaf => (leaf.percent < 1.5 ? 'other' : 'main'),
    allLeafs,
  );

  const otherLeafs = groupedLeafs.other || [];

  if (!otherLeafs.length) {
    return groupedLeafs.main || [];
  }

  const {
    percent: otherPercent,
    tvl: otherTvl,
    valueAtRisk: otherValueAtRisk,
  } = otherLeafs.reduce<Pick<TvlTreemapLeaf, 'percent' | 'tvl' | 'valueAtRisk'>>(
    (acc, cur) => ({
      percent: acc.percent + cur.percent,
      tvl: cur.tvl && acc.tvl && acc.tvl.add(cur.tvl),
      valueAtRisk: cur.valueAtRisk && acc.valueAtRisk && acc.valueAtRisk.add(cur.valueAtRisk),
    }),
    {
      percent: 0,
      tvl: getAmount(0, '$'),
      valueAtRisk: getAmount(0, '$'),
    },
  );

  const otherLeaf: TvlTreemapData = {
    name: {
      assets: 'Other Assets',
      markets: 'Other Markets',
      'account-positions': 'Other Positions',
    }[data.type],
    href:
      data.type === 'markets' && productSlug && versionSlug && network
        ? routeRedirectTo.marketsRatings({
            productSlug,
            network,
            versionSlug,
          }).href
        : null,
    // with other leafs in children the other link not working
    // children: otherLeafs,
    children: [],
    tvl: otherTvl,
    valueAtRisk: otherValueAtRisk,
    percent: Math.max(otherPercent, 5),
  };

  return [...groupedLeafs.main, otherLeaf];
}

function getLeafs(
  data: Props['data'],
  routeRedirectTo: RouteUtils['redirectTo'],
): TvlTreemapLeaf[] {
  const tvlSum =
    data.type === 'assets' || data.type === 'markets'
      ? R.sum(data.info.map(info => amountToAccessorValue(info.partialTotalDeposit)))
      : 0;

  switch (data.type) {
    case 'assets':
      return data.info
        .map<TvlTreemapLeaf>(info => ({
          name: info.token.symbol,
          percent: (amountToAccessorValue(info.partialTotalDeposit) / tvlSum) * 100,
          rating: null,
          icons: [<TokenIcon key={info.token.address} token={info.token} fontSize="inherit" />],
          tvl: info.partialTotalDeposit,
          href: null,
        }))
        .filter(value => !!value.tvl && !value.tvl.isZero());
    case 'markets':
      return data.info
        .map<TvlTreemapLeaf>(info => ({
          name: info.marketName,
          percent: (amountToAccessorValue(info.partialTotalDeposit) / tvlSum) * 100,
          rating: info.rating,
          icons: [
            <MarketIcon
              key={info.marketId}
              productSlug={info.productSlug}
              versionSlug={info.versionSlug}
              network={info.network}
              marketId={info.marketId}
              tokens={info.tokens}
              fontSize="inherit"
            />,
          ],
          tvl: info.partialTotalDeposit,
          href: routeRedirectTo.market({
            marketId: info.marketId,
            productSlug: info.productSlug,
            versionSlug: info.versionSlug,
            network: info.network,
          }).href,
        }))
        .filter(value => !!value.tvl && !value.tvl.isZero());
    case 'account-positions':
      return data.info
        .map<TvlTreemapLeaf>(info => ({
          name: info.marketName,
          percent: info.share,
          rating: toEither(info.rating),
          icons: [
            <ProtocolIcon
              key={info.productSlug}
              productSlug={info.productSlug}
              network={info.network}
              fontSize="inherit"
            />,
          ],
          valueAtRisk: getAmount(info.usdValueAtRisk, '$').mul(decimalsToWei(18)),
          href: routeRedirectTo.position({
            productSlug: info.productSlug,
            versionSlug: info.versionSlug,
            network: info.network,
            accountId: info.accountVid,
            marketId: info.marketVid,
          }).href,
        }))
        .filter(value => !!value.valueAtRisk && !value.valueAtRisk.isZero());
  }
}

const Root = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
}));

const Title = styled('div')(() => ({
  fontSize: 12,
  marginBottom: 48,
}));

const AssetsTreemapWrapper = styled('div')(() => ({
  flexGrow: 1,
  width: '100%',
}));
